import { parseConfig, capitalize, handleize } from '@utilities/theme';

if (!customElements.get('product-overview')) {
  class ProductOverview extends HTMLElement {
    static observedAttributes = [
      "data-enable-sticky-atb",
      "data-enable-sticky-atb-on",
    ];

    constructor() {
      super();
      this.baseUrl = window.Shopify.routes.root + "products/";
      this.elements = {};
      this.selectedOptions = [];
      this.selectedValues = [];
      this.promotion = {};
      this.boundHandleStickyATC = this.handleStickyATC.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.atbButtonMarginOffset = 150;
      // rendering mode
      this.renderingMode = this.dataset.renderingMode || 'server-side';
    }

    async connectedCallback() {
      this.enableStickyATB =
        this.dataset.enableStickyAtb == "true" ? true : false;
      this.enableStickyATBon = this.dataset.enableStickyAtbOn || "render";
      await this.init();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;

      switch (name) {
        case "data-enable-sticky-atb":
          this.enableStickyATB = newValue === "true";
          break;
        case "data-enable-sticky-atb-on":
          this.enableStickyATBon = newValue || "scroll";
          break;
      }

      this.toggleStickyATBFunctionality();
    }

    // simple debug class that is called for every console.log
    // just add the class and the debug logs roll in
    allowDebug = () => {
      return this.classList.contains("debug");
    };

    /**
     *  Set up data, elements, and events.
     *  Dispatch an initialized event on completion
     */
    init = async () => {
      try {
        if (this.allowDebug()) console.log("product-overview.js: init()", this);

        // Set product data (fetch or inline depending on mode)
        this.product = await this.setProduct();

        // If product is null (fetch failed), exit early
        if (!this.product) {
          if (this.allowDebug()) console.log('product-overview.js: init() - Product data is null');
          return;
        }

        if (this.allowDebug())
          console.log("product-overview.js: product", this.product);
        this.noVariant = false;
        this.variant = this.setDefaultVariant();
        if (this.allowDebug())
          console.log("product-overview.js: variant", this.variant);
        this.setElements();
        this.setEventListeners();
        if (this.product?.available) {
          this.setSelectedSwatchData();
          await this.updateState();
        }
        this.classList.add("product-overview--initialized");
        this.dispatchEvent(
          new CustomEvent("product-overview:initialized", {
            bubbles: true,
            detail: {
              productOverview: this,
              product: this.product,
              variant: this.variant,
            },
          })
        );
      } catch (error) {
        console.error("Error initializing component:", error, this);
      }
    };

    /**
     * Component Setters
     */

    /**
     * Sets the product data based on rendering mode
     * Server-side: parses inline JSON from script tag
     * Client-side: fetches JSON from endpoint
     * @returns {Promise<Object>} The product data
     */
    setProduct = async () => {
      // if we are not rendering client-side, return the product data from the JSON script tag
      // if we are rendering client-side, the product will be fetched from the url stored in the data-fetch-url attribute
      if (this.renderingMode !== 'client-side') {
        return parseConfig(this);
      } else {
        // return the product data
        return await this.fetchProduct();
      }
    }

    /**
     * Fetches product data from the product-json endpoint
     * @returns {Promise<Object|null>} The parsed product data or null if fetch fails
     */
    async fetchProduct() {
      try {
        if (this.allowDebug()) {
          console.log('product-overview.js: fetchProduct() - Fetching from:', this.dataset.fetchUrl);
        }

        const response = await fetch(this.dataset.fetchUrl);

        // Check if the response is ok (status 200-299)
        if (!response.ok) {
          if (response.status === 404) {
            console.warn(`Product not found (404): ${this.dataset.fetchUrl}`);
          } else {
            console.warn(`Failed to fetch product (${response.status}): ${this.dataset.fetchUrl}`);
          }
          return null;
        }

        const data = await response.json();

        if (this.allowDebug()) {
          console.log('product-overview.js: fetchProduct() - Successfully fetched product data:', data);
        }

        return data;

      } catch (error) {
        console.error(`Error fetching product data: ${error.message}`, this);
        return null;
      }
    }

    setDefaultVariant = () => {
      if (this.product?.is_parent_product) {
        return this.getVariantById(this.product?.default_child_variant_id);
      } else {
        return this.getVariantById(this.product?.default_variant_id);
      }
    };

    setElements = () => {
      this.elements.productImages = this.querySelector('.product__images');
      this.elements.productMediaContainer = this.querySelector('.product__slider-container');
      this.elements.form = this.querySelector('.product__form');
      this.elements.priceElements = this.querySelectorAll('.product__price-text');
      this.promotionElements = this.querySelectorAll('product-promotion');
      this.elements.colorElement = this.querySelector('.js-color');
      this.elements.swatchContainer = this.querySelector('.js-product-swatches');
      this.elements.swatches = this.querySelectorAll('.js-product-swatches input');
      this.elements.optionGroups = this.querySelectorAll('.js-product-radios');
      this.elements.nonColorGroups = this.querySelectorAll('.js-product-radios:not(.js-product-swatches)');
      this.elements.delimitedOptions = this.querySelectorAll('.js-product-delimited-option');
      this.elements.delimitedOptionGroups = this.querySelectorAll('.js-product-delimited-radios');
      this.elements.atcButton = this.querySelector('button[data-add-to-cart]');
      this.elements.preorderMessage = this.querySelector('.product__preorder-message');
      this.elements.preorderMessageProperty = this.querySelector('input[name="properties[_po_message]"]');
      this.elements.minicartMessage = this.querySelector('.js-product__minicart_message');
      this.elements.minicartMessageProperty = this.querySelector('input[name="properties[_minicart_message]"]');
      this.elements.productActions = this.querySelector('.product__form-actions');
      this.elements.reviewStarsLinks = this.querySelectorAll('.product__reviews-stars-link');
    }

    setEventListeners = () => {
      this.addEventListener("change", this.handleOptionChange.bind(this));
      this.addEventListener(
        "swatch:change",
        this.handleSwatchChange.bind(this)
      );
      this.addEventListener(
        "product-promotion:change",
        this.handlePromotionChange.bind(this)
      );

      // Listen for product-media events
      this.addEventListener(
        "product-media:update-start",
        this.handleMediaUpdateStart.bind(this)
      );
      this.addEventListener(
        "product-media:update-success",
        this.handleMediaUpdateSuccess.bind(this)
      );
      this.addEventListener(
        "product-media:update-error",
        this.handleMediaUpdateError.bind(this)
      );

      // Set a default variant by taking the first variant that has an id matching our expected id.
      if (
        this.getSelectedOptionsTitle() &&
        this.getVariantByTitle(this.getSelectedOptionsTitle())
      ) {
        this.variant = this.getVariantByTitle(this.getSelectedOptionsTitle());
      } else {
        this.variant = this.getVariants()[0];
      }

      // Set event listeners for sticky atb functionality
      this.handleStickyATBChange();

      // Form submission
      this.addEventListener('submit', this.handleFormSubmit.bind(this));

      // Sticky ATC button
      if (this.enableStickyATB) {
        window.addEventListener("scroll", this.boundHandleStickyATC);
      }

      this.toggleStickyATBFunctionality();

      // Trufit stuff - this event should be able to be triggered multiple times, safely.
      const addTrufitHandler = () => {
        try {
          [...this.querySelectorAll('[data-tfc-action="selectSize"]')].forEach(
            (button) => {
              button.onclick = () => {
                if (
                  button.innerText.trim() &&
                  button.innerText.trim().length > 0
                ) {
                  this.selectTrueFitSize([button.innerText.trim()]);
                }
              };
            }
          );
        } catch (error) {
          console.error("Trufit size selection error:", error);
        }
      };

      const handleTruefitSizeUpdate = () => {
        addTrufitHandler();
      };

      document.addEventListener("tfc:widget-rendered", handleTruefitSizeUpdate);
      addTrufitHandler();
    };

    handleFormSubmit = (event) => {
      if(event.target.matches('.product__form')){
        event.preventDefault();
        this.submitToCart();
      }
    }

    handleSwatchChange = async (swatch) => {
      if (!swatch) {
        if (this.allowDebug()) console.log("No swatch data sent");
        return;
      }
      if (this.isParentProduct()) {
        this.getChildProductFromSwatch(swatch);
      } else {
        // Find the matching swatch object based on data-js-handle attribute
        const handleizedColor = swatch.dataset.jsHandle;
        const matchingSwatch = this.product.swatches?.find(
          (swatch) => swatch.handle === handleizedColor
        );
        if (!matchingSwatch) {
          if (this.allowDebug()) console.log("No matching swatch found");
          return;
        }
        this.selectedSwatchData = matchingSwatch;
        this.selectedSwatchElement = swatch;
        const selectedTitle = this.getSelectedOptionsTitle();
        this.variant = this.getVariantByTitle(selectedTitle);
        await this.updateProductMedia();
        this.updateColorLabel();
        await this.updateState();
        this.dispatchEvent(
          new CustomEvent("product-overview:swatch-change", {
            bubbles: true,
            detail: {
              data: this.selectedSwatchData,
              element: this.selectedSwatchElement,
              variant: this.variant,
            },
          })
        );
      }
    };

    setSelectedSwatchData = () => {
      // Start with an empty state.
      this.selectedSwatchElement = null;
      this.selectedSwatchData = {};
      if (!this.hasSwatches()) {
        if (this.allowDebug())
          console.log(
            "product-overview.js: setSelectedSwatchData(), no swatches for the given section.",
            this
          );
        return;
      }
      // Select the currently selected swatch element.
      const selectedSwatchElement =
        this.elements.swatchContainer.querySelector("input:checked");
      if (!selectedSwatchElement) {
        if (this.allowDebug())
          console.log(
            "product-overview.js: setSelectedSwatchData(), unable to find default selected swatch in the DOM.",
            this
          );
        /*
          If we cannot find a selected swatch, find the first input and click it.
          Exit this function, the regular click handler will deal with this.
        */
        const firstSwatch = this.elements.swatchContainer.querySelector(
          "input:first-of-type"
        );
        if (firstSwatch) firstSwatch.click();
        return;
      }
      this.selectedSwatchElement = selectedSwatchElement;
      const handleizedColor = selectedSwatchElement.dataset.jsHandle;
      const matchingSwatch = this.product.swatches?.find(
        (swatch) => swatch.handle === handleizedColor
      );
      if (!matchingSwatch) {
        if (this.allowDebug())
          console.log(
            "product-overview.js: setSelectedSwatchData(), unable to find default selected swatch in the DOM.",
            this
          );
        return;
      }
      this.selectedSwatchData = matchingSwatch;
      this.updateColorLabel();
    };

    setUrl = () => {
      let urlParams = {};
      const locationUrl = window.location;
      // allow existing params to persist in the URL. Code below will re-write any necessary values relating to the component.
      const params = new URLSearchParams(locationUrl.search);
      for (const [key, value] of params) {
        urlParams[key] = value;
      }
      // if we have no variant, clear the URL, update the ATC button
      if (Object.keys(this.variant).length == 0) {
        this.noVariant = true;
        this.updateATCButton(false, "Product not available");
        // if we are not calling this function on a PDP, we can early exit.
        if (!window.location.href.includes(`/products/${this.product?.handle}`))
          return;
        // clear the URL, default it to the current path.
        let newUrl = `${locationUrl.pathname}`;
        // replace the history state
        history.replaceState({}, "", newUrl);
        return;
      }
      if (this.isParentProduct()) {
        if (this.selectedSwatchElement) {
          urlParams.variant = this.selectedSwatchElement.dataset.jsVariantId;
          urlParams.child_product =
            this.selectedSwatchElement.dataset.jsChildProductId;
          urlParams.child_variant = this.variant.id;
        }
      } else {
        urlParams.variant = this.variant.id;
      }
      // at this point let's update the ATC button based on current variant data
      // set add to cart state based on Shopify's availability
      this.noVariant = false;
      this.updateATCButton(this.variant.available_shopify);
      // if we are not calling this function on a PDP, skip it.
      if (!window.location.href.includes(`/products/${this.product?.handle}`))
        return;
      // create a new URL by combining the path and the urlParams
      let newUrl = `${locationUrl.pathname}?${new URLSearchParams(urlParams)}`;
      history.replaceState({}, "", newUrl);
    };

    updateState = async () => {
      if (this.allowDebug())
        console.log(`product-oveview.js updateState`, this.variant);
      this.setUrl();

      this.updateOptionsAvailablility();

      // Reset state if no valid variant found
      if (Object.keys(this.variant).length === 0) {
        const selectedTitle = this.getSelectedOptionsTitle();
        this.updateInventoryAlert("");
        this.updatePreorderText("");
        this.updateMiniCartMessageInput("");
        if (this.allowDebug())
          console.log(
            `product-overview.js unable to find variant under title ${selectedTitle}`,
            this.product.variants
          );
        // tell the world the variant here changed
        this.dispatchEvent(
          new CustomEvent("product-overview:no-variant", {
            bubbles: true,
            detail: {
              productOverview: this,
              requestedVariantTitle: selectedTitle,
            },
          })
        );
        return;
      }

      // Update UI
      this.setPrice(this.variant.price_html);
      this.updateSale(this.variant.on_sale);
      this.updateFinalSale(this.variant.is_final_sale);
      this.updateInventoryAlert(this.variant.inventory_alert_message);
      this.updatePreorderText(this.variant.show_preorder_message);
      this.updateMiniCartMessageInput(this.variant.show_minicart_message);
      this.updateVariantSelectionOnButtons();

      if (this.promotionElements) {
        try {
          await Promise.all(
            Array.from(this.promotionElements).map(async (promotionElement) => {
              const promotionFetchData = {
                url: this.variant.url,
              };

              if (this.product.is_parent_product) {
                promotionFetchData.url = this.variant.parent_variant_url;
                promotionFetchData.backup = {
                  url: this.variant.child_variant_url,
                };
              }

              await promotionElement.updateFetchData(promotionFetchData);
            })
          );
        } catch (error) {
          console.error("Error updating promotions:", error);
        }
      }

      // tell the world the variant here changed
      this.dispatchEvent(
        new CustomEvent("product-overview:variant-change", {
          bubbles: true,
          detail: {
            productOverview: this,
            variant: this.variant,
          },
        })
      );
      /*
        The <truefit-widget> in assets/truefit-widget.js should already cover this.
        This code addition was found via theme sync when pulling down the published theme for deploy GC-1822
        Theoretically, this shouldn't have been needed, and there CAN be more than one <truefit-widget> on the page as the template does allow for multiple.
      */
      try {
        const tfcDiv = document.querySelector('.tfc-fitrec-product');
        if(window.tfcapi && tfcDiv && !tfcDiv.hasChildNodes()) window.tfcapi('calculate');
      } catch (error) {
        console.error('Error calculating TrueFit product:', error);
      }
    };

    /**
     * Component Getters
     */

    getProduct = () => {
      return this.product ?? {};
    };

    hasSwatches = () => {
      return this.elements.swatchContainer && this.product.swatches?.length > 0;
    };

    isStandardProduct = () => {
      return this.product.is_standard_product ?? false;
    };

    isParentProduct = () => {
      return this.product.is_parent_product ?? false;
    };

    isChildProduct = () => {
      return this.product.is_child_product ?? false;
    };

    isSingleVariantProduct = () => {
      return this.product.is_single_variant_product ?? false;
    };

    getPriceRangeHTML = () => {
      return this.product.price_range_html ?? null;
    };

    getVariants = () => {
      return this.product.variants ?? [];
    };

    getSelectedSwatchElement = () => {
      return this.selectedSwatchElement ?? null;
    };

    getSelectedSwatchData = () => {
      return this.selectedSwatchData ?? {};
    };

    getVariantByTitle = (title) => {
      if (!this.product?.variants) return {};
      return this.product.variants.find(variant => handleize(variant.title) === handleize(title) ) ?? {};
    }

    getVariantById = (id) => {
      if (!this.product?.variants) return {};
      return this.product.variants.find((variant) => variant.id === id) ?? {};
    };

    getVariant = () => {
      return this.variant;
    };

    getSelectedOptions = () => {
      return this.querySelectorAll(".js-variant-option:checked");
    };

    getSelectedOptionsTitle = () => {
      this.selectedOptions = this.getSelectedOptions();
      this.selectedValues = Array.from(this.selectedOptions).map(
        (selectedOption) => {
          return selectedOption.dataset.title;
        }
      );
      return this.selectedValues.join(" / ");
    };

    getFetchUrl = (handle) => {
      let dataUrl = `${handle}`;
      if (this.isParentProduct()) {
        if (this.selectedSwatchElement) {
          dataUrl += `?variant=${this.selectedSwatchElement.dataset.jsChildVariantId}`;
        }
      } else {
        dataUrl += `?variant=${this.variant.id}`;
      }
      dataUrl += `&view=product-layout&fetch=true`;
      return dataUrl;
    };

    /**
     * Component Updaters
     */

    updateATCButton = (variantAvailible, overrideText) => {
      if (!this.elements.atcButton) return;
      const atcButtonText =
        this.elements.atcButton.querySelector(".button__text");
      atcButtonText.innerText =
        this.elements?.atcButton?.dataset?.availableText;
      if (variantAvailible) {
        atcButtonText.innerText = this.elements.atcButton.dataset.availableText;
        this.elements.atcButton.removeAttribute("disabled");
        this.elements.atcButton.removeAttribute("aria-disabled");
        this.elements.atcButton.dataset.state = "add-to-bag";
      } else {
        atcButtonText.innerText =
          this.elements.atcButton.dataset.unavailableText;
        this.elements.atcButton.setAttribute("disabled", true);
        this.elements.atcButton.setAttribute("aria-disabled", true);
        this.elements.atcButton.dataset.state = "sold-out";
      }
      if (overrideText) {
        atcButtonText.innerText = overrideText;
      }
      if (
        this.product.third_parties.loop_returns_eligible == false &&
        window.LoopOnstore?.isActive() == true
      ) {
        atcButtonText.innerText = `This product is not eligible for Loop On-Store Returns`;
        this.elements.atcButton.setAttribute("disabled", true);
        this.elements.atcButton.setAttribute("aria-disabled", true);
      }
      if (this.noVariant) {
        this.elements.atcButton.classList.add("no-variant");
      } else {
        this.elements.atcButton.classList.remove("no-variant");
      }
    };

    updateColorLabel = () => {
      if(!this.elements.colorElement) return;
      if(Object.keys(this.selectedSwatchData).length == 0) return;
      this.elements.colorElement.innerHTML = `${capitalize(this.selectedSwatchData.name?.toLowerCase())} <span>${capitalize(this.selectedSwatchData.color_family?.toLowerCase())}</span>`;
    }

    updateOptions = (html) => {
      const optionsHtml = html?.querySelector('.product__options');
      if(!optionsHtml) return;
      // before replacing content, get the current selected options so we can try to deep link.
      const currentOptionGroups = this.querySelectorAll(
        ".js-product-radios:not(.js-product-swatches)"
      );
      let currentSelection = {};
      currentOptionGroups.forEach((group) => {
        const selectedRadio = group.querySelector(
          'input[type="radio"]:checked'
        );
        if (selectedRadio) {
          currentSelection[group.dataset.optionKey] = selectedRadio.value;
        }
      });
      // grab the new HTML we want to inject, and inject it.
      const optionsArea = this.querySelector('.product__options');
      optionsArea.innerHTML = optionsHtml.innerHTML;
      // re-define elements
      this.elements.optionGroups = this.querySelectorAll(".js-product-radios");
      this.elements.nonColorGroups = this.querySelectorAll(
        ".js-product-radios:not(.js-product-swatches)"
      );
      // Try to re-select previously selected options in the new HTML.
      Object.keys(currentSelection).forEach((optionKey) => {
        const valueToSelect = currentSelection[optionKey];
        const newOptionGroup = optionsArea.querySelector(
          `.js-product-radios[data-option-key="${optionKey}"]`
        );
        const matchingRadio = newOptionGroup.querySelector(
          `input[type="radio"][value="${valueToSelect}"]`
        );
        if (matchingRadio) {
          // Uncheck the currently selected radio in the group.
          const currentlySelected = newOptionGroup.querySelector(
            'input[type="radio"]:checked'
          );
          if (currentlySelected) {
            currentlySelected.checked = false;
          }
          // Check the matching radio.
          matchingRadio.checked = true;
        }
      });
    };

    updatePreorderText = (showPreorderMessage) => {
      /*
        If the variant intends to show a preorder message, unhide the messaging and enable the line item property
      */
      if (showPreorderMessage) {
        this.elements.preorderMessage?.classList.remove("hidden");
        this.elements.preorderMessageProperty?.removeAttribute("disabled");
      } else {
        this.elements.preorderMessage?.classList.add("hidden");
        this.elements.preorderMessageProperty?.setAttribute("disabled", true);
      }
    };

    updateMiniCartMessageInput = (enableMinicartMessage) => {
      /*
        if the variant intends to show a minicart message, enable the line item property otherwise disable
      */
      if (enableMinicartMessage) {
        this.elements.minicartMessageProperty?.removeAttribute("disabled");
      } else {
        this.elements.minicartMessageProperty?.setAttribute("disabled", true);
      }
    };

    // update the price elements
    setPrice = (price) => {
      if(this.allowDebug()) console.log('product-overview.js setPrice', price, this.elements.priceElements);
      if(!price) return;
      if(!this.elements.priceElements) return;
      this.elements.priceElements.forEach((priceElement) => {
        priceElement.innerHTML = price;
      });
    }

    setSale = (bool) => {
      this.updateSale(bool);
    };

    updateSale = (show) => {
      const saleProperties = this.querySelectorAll(".sale-property");
      const finalSaleProperties = this.querySelectorAll(".final-sale-property");
      if (show) {
        saleProperties.forEach((prop) => prop.removeAttribute("disabled"));
        finalSaleProperties.forEach((prop) =>
          prop.setAttribute("disabled", true)
        );
      } else {
        saleProperties.forEach((prop) => prop.setAttribute("disabled", true));
      }
    };

    setFinalSale = (bool) => {
      this.updateFinalSale(bool);
    };

    setInventoryAlert = (html) => {
      this.updateInventoryAlert(html);
    };

    updateInventoryAlert = (html) => {
      const inventoryAlertContainer = this.querySelector(
        ".product__inventory-alert-container"
      );
      if (!inventoryAlertContainer) return;
      inventoryAlertContainer.innerHTML = html;
    };

    updateFinalSale = (show) => {
      const saleProperties = this.querySelectorAll(".sale-property");
      const finalSaleProperties = this.querySelectorAll(".final-sale-property");
      if (show) {
        finalSaleProperties.forEach((prop) => prop.removeAttribute("disabled"));
        saleProperties.forEach((prop) => prop.setAttribute("disabled", true));
      } else {
        finalSaleProperties.forEach((prop) =>
          prop.setAttribute("disabled", true)
        );
      }
    };

    updateVariantSelectionOnButtons = () => {
      if (!this.elements.atcButton || !this.elements.productActions) return;

      let selectedVariantPriceHtml = "";

      if(Object.keys(this.variant).length > 0 && this.variant?.price_html) {
        selectedVariantPriceHtml = this.variant.price_html;
      }

      const atcButtonTexts =
        this.elements.productActions.querySelectorAll(".button__text");

      atcButtonTexts.forEach((atcButtonText) => {
        const existingSpan = atcButtonText?.querySelector(".atc-variant-info");
        if (existingSpan) {
          existingSpan.innerHTML = selectedVariantPriceHtml;
        } else {
          let span = document.createElement('span');
          span.className = 'atc-variant-info desktop-hidden';
          span.innerHTML = selectedVariantPriceHtml;
          atcButtonText?.append(span);
        }
      });
    };

    updateProductMedia = async () => {
      if (!this.elements.productMediaContainer) return;

      const mediaUrl =
        this.variant?.media_url ?? this.selectedSwatchData?.media_url;
      if (this.allowDebug())
        console.log(`Updating product media via URL: ${mediaUrl}`);
      if (!mediaUrl) return;

      // Find the product-media component
      const productMediaComponent =
        this.elements.productMediaContainer.querySelector("product-media");
      if (!productMediaComponent) {
        console.warn("❌ Product Overview: No product-media component found");
        return;
      }

      try {
        // Simply pass the media URL to the product-media component
        // It will handle its own fetching, loading states, and transitions
        await productMediaComponent.updateMedia(mediaUrl);

        // Dispatch completion event
        this.dispatchEvent(
          new CustomEvent("product-overview:product-media-updated", {
            bubbles: true,
            detail: {
              variant: this.variant,
              mediaUrl: mediaUrl,
            },
          })
        );

        if (this.allowDebug())
          console.log(
            "✅ Product Overview: Media update delegated to product-media"
          );
      } catch (error) {
        console.error(
          "❌ Product Overview: Error delegating media update:",
          error
        );
      }
    };

    getChildProductFromSwatch = async (swatch) => {
      if (this.allowDebug())
        console.log("product-overview.js getChildProductFromSwatch", swatch);
      try {
        await Promise.all([
          this.getChildProductData(swatch.dataset.jsChildProductUrl),
        ]);
        const selectedTitle = this.getSelectedOptionsTitle();
        this.variant = this.getVariantByTitle(selectedTitle);
        await this.updateProductMedia();
        this.updateColorLabel();
        await this.updateState();
        this.dispatchEvent(
          new CustomEvent("product-overview:swatch-change", {
            bubbles: true,
            detail: {
              data: this.selectedSwatchData,
              element: this.selectedSwatchElement,
              variant: this.variant,
            },
          })
        );
      } catch (error) {
        console.error(error);
      }
    };

    setAddingToBagState = (isAdding) => {
      const button = this.elements.atcButton;
      if (!button) return;
      const buttonText = button.querySelector(".button__text");
      const spinner = button.querySelector(".loading-overlay__spinner");
      button.classList.toggle("adding-to-bag", isAdding);
      button.toggleAttribute("disabled", isAdding);
      button.toggleAttribute("aria-disabled", isAdding);
      button.classList.toggle("loading", isAdding);
      if (buttonText) {
        buttonText.innerText = isAdding
          ? "Adding to bag"
          : button.dataset.availableText;
      }
      if (spinner) {
        spinner.classList.toggle("hidden", !isAdding);
      }
      if (!isAdding) {
        this.updateVariantSelectionOnButtons();
      }
    };

    /**
     * Collect line item properties from hidden inputs
     * @param {HTMLElement} container - Container element to search within (defaults to this element)
     * @returns {Object} Object containing property key-value pairs
     */
    collectLineItemProperties = (container = this) => {
      let properties = {};

      // Search for any hidden inputs that relate to line item properties
      container
        .querySelectorAll(
          `input[type="hidden"][name^="properties["]:not(:disabled)`
        )
        .forEach((el) => {
          const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
          if (propertyName) properties[propertyName] = el.value;
        });

      // Collect promotion properties
      if (this.promotion && Object.keys(this.promotion).length > 0) {
        const promotionProperties = window.theme?.utilities?.getPromotionProperties(this.promotion);
        if (Object.keys(promotionProperties).length > 0) {
          // Go through the promotionProperties return and add each property entry to the properties object
          Object.entries(promotionProperties).forEach(([key, value]) => {
            properties[`${key}`] = value.value;
          });
        }
      }

      if (this.variant.color) {
        properties["_color_id"] = this.variant.color;
      }

      properties["_add_to_cart_component"] = "product-overview";

      if (this.allowDebug()) {
        console.log(
          "🛒 Product Overview: Collected line item properties:",
          properties
        );
      }

      return properties;
    };

    submitToCart = async () => {
      // if the ATC button is not present on the page, we do not want to allow submission.
      if (!this.elements.atcButton) return;
      if (Object.keys(this.variant).length == 0) return;

      try {
        const variantId = this.variant.id;

        let properties = this.collectLineItemProperties();

        const item = { id: variantId, quantity: 1, properties: properties };

        this.setAddingToBagState(true);

        if (this.allowDebug()) {
          console.log("product-overview.js submitToCart", item);
        }

        // Use standardized cart API
        const { addToCart } = await import('@utilities/theme');
        await addToCart(item);

        // Close quickview modal after successful add to cart
        const quickview = this.closest("quickview-modal");
        quickview && quickview.close();

        this.setAddingToBagState(false);
      } catch (error) {
        console.error("Error adding to cart:", error);
        this.setAddingToBagState(false);
      }
    };

    fetchChildProductData = async (handle) => {
      const fetch_url = this.getFetchUrl(handle);
      try {
        const response = await fetch(fetch_url);
        const responseHTML = await response.text();
        return responseHTML;
      } catch (error) {
        console.error(error);
      }
    };

    getChildProductData = async (handle) => {
      if (this.allowDebug())
        console.log("product-overview.js getChildProductData", handle);
      if (window.outerWidth > 767) {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
      const childProductResponse = await this.fetchChildProductData(handle);
      const responseHTML = new DOMParser().parseFromString(
        childProductResponse,
        "text/html"
      );
      if (responseHTML) {
        const childProductOverview =
          responseHTML.querySelector("product-overview");
        this.updateOptions(childProductOverview);
        this.setElements();
        this.setSelectedSwatchData();
      }
      return Promise.resolve();
    };

    handleStickyATBChange = () => {
      const newEnableStickyATB =
        this.getAttribute("data-enable-sticky-atb") == "true" ? true : false;
      if (newEnableStickyATB != this.enableStickyATB) {
        this.enableStickyATB = newEnableStickyATB;
        this.toggleStickyATBFunctionality();
      }
    };

    toggleStickyATBFunctionality = () => {
      if (this.enableStickyATB) {
        this.enableStickyATBEvents();
        this.handleStickyATC();
      } else {
        this.disableStickyATBEvents();
      }
    };

    enableStickyATBEvents = () => {
      window.addEventListener("scroll", this.boundHandleStickyATC);
      window.addEventListener("resize", this.boundHandleResize);
      this.handleStickyATC();
    };

    disableStickyATBEvents = () => {
      window.removeEventListener("scroll", this.boundHandleStickyATC);
      window.removeEventListener("resize", this.boundHandleResize);
      this.resetStickyATBStyles();
    };

    resetStickyATBStyles = () => {
      if (this.elements.productActions) {
        this.elements.productActions.classList.remove(
          "product__form-actions--sticky",
          "product__form-actions--sticky-stop"
        );
      }
    };

    handleStickyATC = () => {
      if (!this.elements.productActions || !this.enableStickyATB) return;

      const mainContent = document.getElementById('main-content');
      if (!mainContent) return;

      const mainContentRect = mainContent.getBoundingClientRect();
      const buttonWrapperRect =
        this.elements.productActions.getBoundingClientRect();
      const scrollTop = window.scrollY;
      const totalHeaderHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--header-height"
          ),
          10
        ) || 92;

      // First check if we should be sticky at all based on scroll position
      const shouldBeSticky =
        scrollTop > buttonWrapperRect.top + scrollTop - totalHeaderHeight;

      if (!shouldBeSticky) {
        this.elements.productActions.classList.remove(
          "product__form-actions--sticky",
          "product__form-actions--sticky-stop"
        );
        return;
      }

      // Then check main content visibility
      const mainContentBottom = mainContentRect.bottom;
      const viewportHeight = window.innerHeight;

      if (mainContentBottom <= viewportHeight) {
        this.elements.productActions.classList.add(
          "product__form-actions--sticky-stop"
        );
        this.elements.productActions.classList.remove(
          "product__form-actions--sticky"
        );
      } else {
        this.elements.productActions.classList.add(
          "product__form-actions--sticky"
        );
        this.elements.productActions.classList.remove(
          "product__form-actions--sticky-stop"
        );
      }
    };

    handleResize = () => {
      this.handleStickyATC();
    };

    limitStickyATCScroll = () => {
      const pageFooter = document.getElementById("shopify-section-footer");
      if (!pageFooter || !this.elements.productActions) return;
      const topOfFooter = pageFooter.getBoundingClientRect().top;
      if (topOfFooter <= window.innerHeight + this.atbButtonMarginOffset) {
        this.elements.productActions.classList.add(
          "product__form-actions--sticky-stop"
        );
      } else {
        this.elements.productActions.classList.remove(
          "product__form-actions--sticky-stop"
        );
      }
    };

    handlePromotionChange = (event) => {
      // Check if the event's triggering element is a descendant of this component
      if (this.contains(event.detail.triggeringElement)) {
        this.promotion = event.detail.promotion;
      }
    };

    selectTrueFitSize = (sizes) => {
      if (!sizes || !this.querySelectorAll(".js-product-radios").length) return;

      const sizeGroup = this.querySelector(
        '.js-product-radios[data-option="Size"]'
      );
      if (!sizeGroup) {
        return; // Early exit if no size group found
      }

      // normalize recommended size values
      const size_suggested = handleize(sizes.suggested);
      const size_alternative = handleize(sizes.alternative);

      const sizeInputs = sizeGroup.querySelectorAll("input");
      sizeInputs.forEach((sizeInput) => {
        // normalize size input value
        const size_input_value = handleize(sizeInput.value);
        if (size_input_value === size_suggested || size_input_value === size_alternative) {
          sizeInput.checked = true;
          sizeInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    };

    calculateDelimitedOption = (delimitedOption) => {
      if (!delimitedOption) return { valid: false };

      // Find the delimited option group container
      const container = this.querySelector(
        `.js-product-delimited-option[data-option="${delimitedOption}"]`
      );
      if (!container) return { valid: false };

      // Collect all selected parts
      const selectedParts = new Map();
      container
        .querySelectorAll(".js-variant-delimited-option:checked")
        .forEach((selected) => {
          const group = selected.closest(".js-product-delimited-radios");
          if (group?.dataset.optionMap) {
            selectedParts.set(group.dataset.optionMap, selected.value);
          }
        });

      if (this.allowDebug()) {
        console.log(
          `calculateDelimitedOption: collected parts for ${delimitedOption}:`,
          selectedParts
        );
      }

      // Find variant option that matches all selected parts
      const matchingVariantOption = Array.from(
        container.querySelectorAll(".js-variant-option")
      ).find((option) =>
        Array.from(selectedParts.entries()).every(
          ([attr, value]) => option.getAttribute(attr) === value
        )
      );

      if (!matchingVariantOption) {
        if (this.allowDebug()) {
          console.log(
            `calculateDelimitedOption: no matching complete option found for ${delimitedOption}`
          );
        }
        return { valid: false };
      }

      // Update selection if necessary
      const currentlySelected = container.querySelector(
        ".js-variant-option:checked"
      );
      if (currentlySelected !== matchingVariantOption) {
        if (currentlySelected) {
          currentlySelected.checked = false;
        }
        matchingVariantOption.checked = true;
      }

      return { valid: true };
    };

    generateTitleFromSelection = (selectionState) => {
      // Get all option groups in order (by their optionKey which will be option1, option2, option3)
      const optionGroups = Array.from(this.elements.optionGroups)
        .sort((a, b) => {
          const aKey = parseInt(a.dataset.optionKey.replace('option', ''),10);
          const bKey = parseInt(b.dataset.optionKey.replace('option', ''),10);
          return aKey - bKey;
        }
      );

      const values = [];

      // Go through each group in order
      optionGroups.forEach((group) => {
        const isDelimited = group.classList.contains(
          "js-product-delimited-option"
        );

        if (isDelimited) {
          // For delimited options, find the selected complete variant option
          const selectedVariantOption = group.querySelector(
            ".js-variant-option:checked"
          );
          if (selectedVariantOption) {
            values.push(selectedVariantOption.value);
          }
        } else {
          // For standard options, use the value from selectionState
          const optionKey = group.dataset.optionKey;
          if (selectionState[optionKey]) {
            values.push(selectionState[optionKey]);
          }
        }
      });

      return values.join(" / ");
    };

    updateOptionsAvailablility = () => {
      // Get current standard option selections
      let currentSelection = {};
      const standardOptionGroups = Array.from(
        this.elements.optionGroups
      ).filter(
        (group) => !group.classList.contains("js-product-delimited-option")
      );

      standardOptionGroups.forEach((group) => {
        const selectedRadio = group.querySelector(
          'input[type="radio"]:checked'
        );
        if (selectedRadio) {
          currentSelection[group.dataset.optionKey] = selectedRadio.value;
        }
      });

      // Get all selected delimited options
      const delimitedValues = new Map();
      this.elements.delimitedOptions?.forEach((delimitedOption) => {
        const selectedOption = delimitedOption.querySelector(
          ".js-variant-option:checked"
        );
        if (selectedOption) {
          delimitedValues.set(
            delimitedOption.dataset.optionKey,
            selectedOption.value
          );
        }
      });

      // Update standard options availability
      standardOptionGroups.forEach((group) => {
        const radios = group.querySelectorAll('input[type="radio"]');
        const optionKey = group.dataset.optionKey;

        radios.forEach((radio) => {
          // Create a complete test selection including this option
          const testSelection = {
            ...currentSelection,
            [optionKey]: radio.value,
          };

          // Generate the title that would result from this combination
          const testTitle = this.generateTitleFromSelection(testSelection);

          // Check if any variant exists with this exact title
          const exactVariantExists = this.product.variants.some(variant =>
            handleize(variant.title) === handleize(testTitle)
          );

          const isImpossible = !exactVariantExists;
          const isAvailable = !isImpossible && this.product.variants.some(variant => {
            return handleize(variant.title) === handleize(testTitle) && variant.available_shopify;
          });

          this.updateOptionClasses(radio, isImpossible, !isAvailable);
        });
      });

      // Update delimited options availability
      this.elements.delimitedOptions?.forEach((delimitedOption) => {
        const allDelimitedParts = delimitedOption.querySelectorAll(
          ".js-variant-delimited-option"
        );
        const variantOptions = Array.from(
          delimitedOption.querySelectorAll(".js-variant-option")
        );
        const optionKey = delimitedOption.dataset.optionKey;

        allDelimitedParts.forEach((option) => {
          const group = option.closest(".js-product-delimited-radios");
          const groupMapping = group?.dataset.optionMap;
          if (!groupMapping) return;

          // Get current delimited selections for this option group
          const currentDelimitedSelections = new Map();
          delimitedOption
            .querySelectorAll(".js-variant-delimited-option:checked")
            .forEach((selected) => {
              const selectedGroup = selected.closest(
                ".js-product-delimited-radios"
              );
              if (selectedGroup !== group) {
                currentDelimitedSelections.set(
                  selectedGroup.dataset.optionMap,
                  selected.value
                );
              }
            });
          currentDelimitedSelections.set(groupMapping, option.value);

          // Find matching complete option
          const matchingVariantOption = variantOptions.find((variantOption) => {
            return Array.from(currentDelimitedSelections.entries()).every(
              ([attr, value]) => {
                return variantOption.getAttribute(attr) === value;
              }
            );
          });

          if (matchingVariantOption) {
            // Test the complete combination
            const testSelection = { ...currentSelection };
            testSelection[optionKey] = matchingVariantOption.value;

            const testTitle = this.generateTitleFromSelection(testSelection);

            const exactVariantExists = this.product.variants.some(variant =>
              handleize(variant.title) === handleize(testTitle)
            );

            const isAvailable = exactVariantExists && this.product.variants.some(variant =>
              handleize(variant.title) === handleize(testTitle) && variant.available_shopify
            );

            this.updateOptionClasses(option, !exactVariantExists, !isAvailable);
          } else {
            // No matching complete option found - this is an impossible combination
            this.updateOptionClasses(option, true, false);
          }
        });
      });

      this.dispatchEvent(
        new CustomEvent("product-overview:option-availability-change", {
          bubbles: true,
        })
      );
    };

    // Helper to update option classes consistently
    updateOptionClasses(element, impossible, unavailable) {
      element.classList.toggle("impossible-combination", impossible);
      element.classList.toggle(
        "option-unavailable",
        unavailable && !impossible
      );

      if (impossible) {
        element.setAttribute("tabindex", "-1");
      } else {
        element.removeAttribute("tabindex");
      }
    }

    handleOptionChange = async (event) => {
      const target = event.target;

      // Handle delimited option changes
      const delimitedGroup = target.closest(".js-product-delimited-radios");
      if (
        delimitedGroup &&
        target.classList.contains("js-variant-delimited-option")
      ) {
        event.preventDefault();

        // First check if this is an impossible selection
        if (target.classList.contains("impossible-combination")) {
          this.variant = {};
          this.setUrl();
          this.updateOptionsAvailablility();
          return;
        }

        const delimitedOption = delimitedGroup.dataset.option;
        const result = this.calculateDelimitedOption(delimitedOption);

        if (!result.valid) {
          // If not valid, clear the variant and update URL
          this.variant = {};
          this.setUrl();
          this.updateOptionsAvailablility();
          return;
        }

        await this.updateState();
        return;
      }

      // Handle standard option changes
      if (target.closest(".js-product-radios:not(.js-product-swatches)")) {
        event.preventDefault();
        const selectedTitle = this.getSelectedOptionsTitle();
        this.variant = this.getVariantByTitle(selectedTitle);
        await this.updateState();
        return;
      }

      // Handle swatch changes
      if (target.closest(".js-product-radios.js-product-swatches")) {
        event.preventDefault();
        this.handleSwatchChange(target);
        return;
      }
    };

    handleMediaUpdateStart = (event) => {
      if (this.allowDebug()) {
        console.log("🖼️ Product Overview: Media update started", event.detail);
      }
    };

    handleMediaUpdateSuccess = (event) => {
      if (this.allowDebug()) {
        console.log(
          "✅ Product Overview: Media update completed",
          event.detail
        );
      }
    };

    handleMediaUpdateError = (event) => {
      console.error("❌ Product Overview: Media update failed", event.detail);
    };
  }

  customElements.define("product-overview", ProductOverview);
}
